import React from 'react';
import { BarChart, Trash2, FileText, Layers } from 'lucide-react';
import { formatCurrency, formatDate } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для истории расчетов командировок
 */
const HistorySidebar = ({ 
  savedCalculations, 
  deleteSavedCalculation, 
  loadSavedCalculation 
}) => {
  // Если нет сохраненных расчетов
  if (!savedCalculations || savedCalculations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <FileText className="w-5 h-5 mr-2 text-indigo-500" />
          История расчетов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные расчеты командировок.
        </p>
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
          <p className="text-xs text-blue-700 dark:text-blue-300 flex items-center">
            <Layers className="w-4 h-4 mr-2 flex-shrink-0" />
            Сохраняйте расчеты, чтобы сравнивать их и быстро возвращаться к ним позднее.
          </p>
        </div>
      </div>
    );
  }

  // Форматирование даты
  const formatDateString = (dateStr) => {
    try {
      return formatDate(new Date(dateStr), 'date');
    } catch (error) {
      return 'Некорректная дата';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <FileText className="w-5 h-5 mr-2 text-indigo-500" />
          История расчетов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedCalculations.length} {savedCalculations.length === 1 ? 'запись' : 
           savedCalculations.length < 5 ? 'записи' : 'записей'}
        </span>
      </div>

      <div className="space-y-4">
        {savedCalculations.slice(0, 5).map((calculation) => (
          <div 
            key={calculation.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {calculation.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedCalculation(calculation.id)}
                  title="Загрузить расчет"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedCalculation(calculation.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Дата:</span>
                <span>{formatDateString(calculation.date)}</span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Место:</span>
                <span className="truncate max-w-[140px]">{calculation.results.destinationName}</span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Период:</span>
                <span>{calculation.results.tripDurationDays} дн.</span>
              </div>
              <div className="flex justify-between">
                <span>Итого:</span>
                <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                  {formatCurrency(calculation.results.totalExpenses, 0)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {savedCalculations.length > 5 && (
        <div className="mt-4 text-center">
          <Button
            variant="link"
            size="sm"
            icon={<BarChart className="w-4 h-4" />}
          >
            Показать еще {savedCalculations.length - 5} расчетов
          </Button>
        </div>
      )}

      <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800/40 rounded-lg border border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-600 dark:text-gray-400 flex items-center">
          <FileText className="w-4 h-4 mr-2 flex-shrink-0" />
          Данные расчетов сохраняются локально в вашем браузере и будут доступны при следующем посещении страницы.
        </p>
      </div>
    </div>
  );
};

export default HistorySidebar;